import { Link } from 'react-router-dom';
import { Img, SectionHeader } from '@/components/ui';
import type { Brand } from '@/types';

type SimilarBrand = Pick<Brand, 'id' | 'name' | 'urlPath' | 'logo' | 'seo'>;

export interface SimilarBrandsProps {
  brands: readonly SimilarBrand[];
  className?: string;
}

/**
 * Similar brands chips/cards (web plan W3.6): ≤ 12 other indexable brands
 * sharing a category. Each chip links to the brand's hub via `urlPath`.
 * Only brands whose own hub is indexable are shown (the backend already
 * filters on `seo.indexable`, but we guard on the client too).
 */
export function SimilarBrands({ brands, className }: SimilarBrandsProps) {
  // Backend already filters to indexable brands, but the SDL says Brand.seo is
  // optional — guard to be safe.
  const visible = brands.filter(
    (b) => b.urlPath && b.seo?.indexable !== false,
  );
  if (visible.length === 0) return null;

  return (
    <section aria-label="Similar brands" className={className}>
      <SectionHeader title="Similar Brands" />
      <ul className="mt-4 flex flex-wrap gap-2" role="list">
        {visible.map((brand) => (
          <li key={brand.id}>
            <Link
              to={brand.urlPath!}
              className="inline-flex items-center gap-2 rounded-full border border-line bg-card px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:border-primary hover:text-primary"
            >
              {brand.logo && (
                <Img
                  media={brand.logo}
                  alt=""
                  className="h-5 w-5 rounded-full object-contain"
                />
              )}
              {brand.name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default SimilarBrands;
